<template>
  <div>
    <Header />

    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h5 class="page-title">Manage Expenses</h5>
        <p class="page-sub">Track and manage all expense records</p>
      </div>
      <div class="page-actions">

        <!-- Search -->
        <div class="search-wrap">
          <font-awesome-icon icon="search" class="search-icon" />
          <input type="text" class="search-input" placeholder="Search expenses..." @input="tableData.page = 1; fetchData()" v-model="tableData.search" />
        </div>

        <!-- Status Filter -->
        <select v-model="tableData.status" @change="tableData.page = 1; fetchData()" class="filter-select">
          <option :value="JSON.parse(status.pending)">Pending</option>
          <option :value="JSON.parse(status.approved)">Approved</option>
          <option :value="JSON.parse(status.disapproved)">Rejected</option>
          <option :value="JSON.parse(status.all)">All Statuses</option>
        </select>

        <!-- Export -->
        <div class="dropdown">
          <button class="btn-outline dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
            <font-awesome-icon icon="download" /> Export
          </button>
          <ul class="dropdown-menu dropdown-menu-end">
            <li><a class="dropdown-item" href="#" @click.prevent="openExportModal('pdf')">
              <font-awesome-icon icon="file-pdf" class="me-2 text-danger" /> PDF
            </a></li>
            <li><a class="dropdown-item" href="#" @click.prevent="openExportModal('csv')">
              <font-awesome-icon icon="file-csv" class="me-2 text-success" /> CSV
            </a></li>
          </ul>
        </div>

        <!-- Bulk Action -->
        <div class="dropdown" v-if="selectedModel.length > 0">
          <button class="btn-outline dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
            <font-awesome-icon icon="tasks" /> Bulk Action
            <span class="selection-badge">{{ selectedModel.length }}</span>
          </button>
          <ul class="dropdown-menu dropdown-menu-end">
            <li>
              <button class="dropdown-item" @click="getUpdateType('approve')"
                data-bs-toggle="modal" data-bs-target="#bulkModal"
                :disabled="permission.approve_expense != 1">
                <font-awesome-icon icon="check" class="me-2 text-success" /> Approve
              </button>
            </li>
            <li>
              <button class="dropdown-item" @click="getUpdateType('disapprove')"
                data-bs-toggle="modal" data-bs-target="#bulkModal"
                :disabled="permission.approve_expense != 1">
                <font-awesome-icon icon="times" class="me-2 text-warning" /> Reject
              </button>
            </li>
            <li><hr class="dropdown-divider" /></li>
            <li>
              <button class="dropdown-item text-danger" @click="getUpdateType('delete')"
                data-bs-toggle="modal" data-bs-target="#bulkModal"
                :disabled="permission.delete_expense != 1">
                <font-awesome-icon icon="trash" class="me-2" /> Delete
              </button>
            </li>
          </ul>
        </div>

        <!-- Create Expense -->
        <router-link :to="$url('/create-expense')" class="btn-primary-action"
          :class="permission.create_expense != 1 ? 'disabled' : ''">
          <font-awesome-icon icon="plus" /> Create Expense
        </router-link>

      </div>
    </div>

    <!-- Data Card -->
    <div class="data-card">

      <!-- Loading -->
      <div v-if="loading" class="state-block">
        <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
        <p class="mt-3 text-muted-cell">Loading expenses...</p>
      </div>

      <!-- Empty -->
      <div v-else-if="expenses.length === 0" class="state-block">
        <div class="state-icon state-icon--orange">
          <font-awesome-icon icon="receipt" />
        </div>
        <p class="state-title">No expenses found</p>
        <p class="state-sub">No expense records match your current filter.</p>
        <router-link v-if="permission.create_expense == 1" :to="$url('/create-expense')" class="btn-primary-action mt-2">
          <font-awesome-icon icon="plus" /> Create Expense
        </router-link>
      </div>

      <!-- Table -->
      <template v-else>

        <div class="table-toolbar">
          <span class="table-count">
            Showing <strong>{{ pagination.from }}–{{ pagination.to }}</strong> of <strong>{{ pagination.total }}</strong>
          </span>
          <div class="toolbar-right">
            <label class="table-count">Rows:</label>
            <select v-model="tableData.length" @change="tableData.page = 1; fetchData()" class="rows-select">
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="200">200</option>
              <option value="500">500</option>
              <option value="1000">1000</option>
            </select>
          </div>
        </div>

        <div class="table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                <th style="width:48px; padding-left:30px;">
                  <input type="checkbox" v-model="allSelected" @change="selectAll" class="form-check-input" />
                </th>
                <th>#</th>
                <th>Reference</th>
                <th>Category</th>
                <th class="text-right">Amount</th>
                <th>Payment Type</th>
                <th>Status</th>
                <th>Date</th>
                <th class="col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in expenses" :key="row.id">

                <td style="padding-left:30px;">
                  <input type="checkbox" v-model="selectedModel" :value="row.id" class="form-check-input" />
                </td>

                <td class="text-muted-cell">{{ index + 1 }}</td>

                <td>
                  <span class="batch-code">{{ row.reference }}</span>
                </td>

                <td class="text-muted-cell">{{ row.ledger?.name || '—' }}</td>

                <td class="text-right fw-bold amount-value">
                  {{ row?.currency?.symbol }}{{ formatPrice(row.amount) }}
                </td>

                <td class="text-muted-cell">{{ row.payment_type?.name || '—' }}</td>

                <td>
                  <span class="status-badge"
                    :class="{ 'status--pending': row.status == 0, 'status--approved': row.status == 1, 'status--rejected': row.status == 2 }">
                    {{ getStatus(row) }}
                  </span>
                </td>

                <td class="text-muted-cell">{{ formatDate(row.transaction_date) }}</td>

                <td class="col-actions">
                  <div class="dropdown">
                    <button class="action-dropdown-btn" type="button" data-bs-toggle="dropdown" data-bs-strategy="fixed" aria-expanded="false">
                      <font-awesome-icon icon="ellipsis-v" />
                    </button>
                    <ul class="dropdown-menu dropdown-menu-end action-menu">
                      <li>
                        <button class="action-menu-item"
                          :disabled="permission.view_expense != 1"
                          @click="viewDetailsModal(index)">
                          <font-awesome-icon icon="eye" class="action-menu-icon action-menu-icon--view" />
                          View Details
                        </button>
                      </li>
                      <li v-if="row.status != 1">
                        <router-link
                          :to="permission.update_expense == 1 ? '/expense/detail/' + row.reference : '#'"
                          class="action-menu-item"
                          :class="permission.update_expense != 1 ? 'action-menu-item--disabled' : ''">
                          <font-awesome-icon icon="pen" class="action-menu-icon action-menu-icon--edit" />
                          Update Expense
                        </router-link>
                      </li>
                      <li v-if="row.status == 0 && permission.approve_expense == 1">
                        <button class="action-menu-item" @click="openExpenseModal(index, 'approve')">
                          <font-awesome-icon icon="check" class="action-menu-icon action-menu-icon--approve" />
                          Approve
                        </button>
                      </li>
                      <li v-if="row.status == 0 && permission.approve_expense == 1">
                        <button class="action-menu-item" @click="openExpenseModal(index, 'reject')">
                          <font-awesome-icon icon="ban" class="action-menu-icon action-menu-icon--reject" />
                          Reject
                        </button>
                      </li>
                      <li v-if="row.status != 1 && permission.delete_expense == 1">
                        <hr class="dropdown-divider" />
                        <button class="action-menu-item action-menu-item--danger"
                          @click="getOneModal(row)"
                          data-bs-toggle="modal" data-bs-target="#singleModal">
                          <font-awesome-icon icon="trash" class="action-menu-icon" />
                          Delete
                        </button>
                      </li>
                    </ul>
                  </div>
                </td>

              </tr>
            </tbody>
          </table>
        </div>

        <div class="table-footer">
          <pagination :pagination="pagination" @prev="tableData.page = pagination.currentPage - 1; fetchData()" @next="tableData.page = pagination.currentPage + 1; fetchData()" />
        </div>

      </template>
    </div>


    <!-- ── Approve / Reject Modal ── -->
    <div class="modal fade" id="approve_modal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered modal-sm">
        <div class="modal-content modal-clean">
          <div class="modal-icon-wrap">
            <div class="modal-icon" :class="status_id == 1 ? 'modal-icon--success' : 'modal-icon--warning'">
              <font-awesome-icon :icon="status_id == 1 ? 'check' : 'times'" />
            </div>
          </div>
          <div class="modal-body-clean">
            <p class="modal-heading">{{ status_id == 1 ? 'Approve' : 'Reject' }} Expense?</p>
            <p class="modal-subtext">
              Are you sure you want to {{ status_id == 1 ? 'approve' : 'reject' }} expense
              <strong>{{ currentData.reference }}</strong>?
            </p>
          </div>
          <div class="modal-actions">
            <button type="button" class="modal-btn modal-btn--cancel" @click="closeApproveModal">Cancel</button>
            <button type="button"
              class="modal-btn modal-btn--confirm"
              :class="status_id == 1 ? 'modal-btn--success' : 'modal-btn--warning'"
              :disabled="crudLoading"
              @click="approveExpense">
              <span v-if="crudLoading" class="spinner-border spinner-border-sm me-1" role="status"></span>
              <font-awesome-icon v-else :icon="status_id == 1 ? 'check' : 'times'" class="me-1" />
              {{ crudLoading ? 'Processing...' : status_id == 1 ? 'Yes, Approve' : 'Yes, Reject' }}
            </button>
          </div>
        </div>
      </div>
    </div>


    <!-- ── View Detail Modal ── -->
    <div v-if="currentData" class="modal fade" id="view_detail" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable">
        <div class="modal-content modal-clean">
          <div class="modal-header-clean">
            <div class="modal-title-icon" :class="{
              'modal-icon--success-bg': currentData.status == 1,
              'modal-icon--warning-bg': currentData.status == 0,
              'modal-icon--danger-bg':  currentData.status == 2,
            }">
              <font-awesome-icon icon="receipt" />
            </div>
            <div>
              <h6 class="modal-clean-title">Expense Detail</h6>
              <p class="modal-clean-sub">{{ currentData.reference }}</p>
            </div>
            <span class="status-badge ms-auto"
              :class="{ 'status--pending': currentData.status == 0, 'status--approved': currentData.status == 1, 'status--rejected': currentData.status == 2 }">
              {{ getStatus(currentData) }}
            </span>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close" style="margin-left:8px"></button>
          </div>
          <div class="modal-body">
            <div class="detail-grid">
              <div class="detail-item">
                <span class="detail-label">Reference</span>
                <span class="detail-value"><span class="batch-code">{{ currentData.reference }}</span></span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Expense Date</span>
                <span class="detail-value">{{ formatDate(currentData.transaction_date) }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Category</span>
                <span class="detail-value">{{ currentData?.ledger?.name || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Currency</span>
                <span class="detail-value">{{ currentData?.currency?.name }} ({{ currentData?.currency?.symbol }})</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Amount</span>
                <span class="detail-value fw-bold amount-value">{{ currentData?.currency?.symbol }}{{ formatPrice(currentData?.amount) }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Payment Type</span>
                <span class="detail-value">{{ currentData?.payment_type?.name || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Bank</span>
                <span class="detail-value">{{ currentData?.bank?.bank_name || '—' }}</span>
              </div>
              <div class="detail-item detail-item--full">
                <span class="detail-label">Title</span>
                <span class="detail-value">{{ currentData?.title || '—' }}</span>
              </div>
              <div class="detail-item detail-item--full" v-if="currentData?.description">
                <span class="detail-label">Description</span>
                <span class="detail-value detail-value--muted">{{ currentData.description }}</span>
              </div>
              <div class="detail-divider"></div>
              <div class="detail-item">
                <span class="detail-label">Created By</span>
                <span class="detail-value">{{ currentData?.created_user?.first_name }} {{ currentData?.created_user?.last_name }}</span>
              </div>
              <div class="detail-item" v-if="currentData.status != 0">
                <span class="detail-label">{{ currentData.status == 1 ? 'Approved By' : 'Rejected By' }}</span>
                <span class="detail-value">{{ currentData?.approved_user?.first_name }} {{ currentData?.approved_user?.last_name }}</span>
              </div>
            </div>
          </div>
          <div class="modal-footer border-0 pt-0">
            <button type="button" class="btn-outline" data-bs-dismiss="modal">Close</button>
          </div>
        </div>
      </div>
    </div>


    <!-- Single Delete -->
    <SingleDeleteModal
      :modalId="'singleModal'"
      @deleteSingle="deleteExpense"
      :modalText="'Are you sure you want to delete this expense?'"
      :action="'deleteSingle'"
    />

    <!-- Bulk Action -->
    <BulkDeleteModal
      :modalId="'bulkModal'"
      @deleteBulk="bulkUpdate"
      :modalText="'Are you sure you want to perform this bulk action on selected expenses?'"
    />

    <!-- ── Export Column Picker ── -->
    <transition name="fade">
      <div v-if="showExportModal" class="col-picker-backdrop" @click.self="showExportModal = false">
        <div class="col-picker-modal">
          <div class="col-picker-header">
            <div>
              <h3 class="col-picker-title">Select Columns</h3>
              <p class="col-picker-sub">Choose what to include in your {{ pendingExportFmt.toUpperCase() }} export</p>
            </div>
            <button class="col-picker-close" @click="showExportModal = false"><font-awesome-icon icon="times" /></button>
          </div>
          <div class="col-picker-actions">
            <button class="col-picker-link" @click="selectedExportCols = exportColumnDefs.map(c => c.key)">Select all</button>
            <span class="col-picker-sep">·</span>
            <button class="col-picker-link" @click="selectedExportCols = []">Deselect all</button>
            <span class="col-picker-count">{{ selectedExportCols.length }} of {{ exportColumnDefs.length }} selected</span>
          </div>
          <div class="col-picker-grid">
            <label v-for="col in exportColumnDefs" :key="col.key" class="col-picker-item">
              <input type="checkbox" :value="col.key" v-model="selectedExportCols" class="col-picker-check" />
              <span class="col-picker-label">{{ col.label }}</span>
            </label>
          </div>
          <div class="col-picker-footer">
            <button class="btn-outline" @click="showExportModal = false">Cancel</button>
            <button class="btn-primary-action" :disabled="selectedExportCols.length === 0 || exportLoading" @click="doExport()">
              <font-awesome-icon :icon="exportLoading ? 'spinner' : pendingExportFmt === 'pdf' ? 'file-pdf' : 'file-csv'" :spin="exportLoading" />
              Export {{ pendingExportFmt.toUpperCase() }}
            </button>
          </div>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { reusableMixin } from '../../../lib/reusableMixin';
import { useStore } from 'vuex';
import moment from 'moment';
import Header from './component/header.vue';
import BulkDeleteModal  from '../../../components/bulkDeleteModal.vue';
import SingleDeleteModal from '../../../components/ActionModal.vue';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

export default {
  components: { Header, BulkDeleteModal, SingleDeleteModal },
  mixins: [reusableMixin],

  data() {
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

    return {
      print_content: [],
      headerArrayColumn: ['S/N','Reference','Ledger','Currency','Amount','Bank','Payment Type','Created Date','Status'],

      status: {
        all:         "['0','1','2']".replace(/'/g, '"'),
        pending:     "['0']".replace(/'/g, '"'),
        approved:    "['1']".replace(/'/g, '"'),
        disapproved: "['2']".replace(/'/g, '"'),
      },

      expenses:      [],
      expense_id:    null,
      status_id:     0,
      loading:       false,
      crudLoading:   false,
      currentData:   {},
      allSelected:   false,
      selectedModel: [],
      type:          '',

      tableData: { page: 1,
        start_date: sixMonthsAgo,
        end_date:   new Date(),
        draw: 0, length: 5, search: '', column: 0, dir: 'desc',
        status: [0,1,2],
      },
      pagination: { lastPage:'', currentPage:'', total:'', lastPageUrl:'', nextPageUrl:'', prevPageUrl:'', from:'', to:'' },
      toast:  useToast(),
      store:  useStore(),
      showExportModal: false, pendingExportFmt: 'csv', selectedExportCols: [], exportLoading: false,
      exportColumnDefs: [
        { key: 'reference',    label: 'Reference',    pdfWidth: 90 },
        { key: 'category',     label: 'Category',     pdfWidth: 100 },
        { key: 'amount',       label: 'Amount',       pdfWidth: 80 },
        { key: 'currency',     label: 'Currency',     pdfWidth: 75 },
        { key: 'payment_type', label: 'Payment Type', pdfWidth: 95 },
        { key: 'bank',         label: 'Bank',         pdfWidth: 85 },
        { key: 'status',       label: 'Status',       pdfWidth: 80 },
        { key: 'date',         label: 'Date',         pdfWidth: 80 },
        { key: 'created_by',   label: 'Created By',   pdfWidth: 76 },
      ],
    };
  },

  computed: {
    permission() { return this.store.state.getCurrentUserPermission; },
  },

  created() { this.fetchData(); },

  methods: {
    openExportModal(format) {
      this.pendingExportFmt   = format;
      this.selectedExportCols = this.exportColumnDefs.map(c => c.key);
      this.showExportModal    = true;
    },

    getExpenseValue(row, key) {
      switch (key) {
        case 'reference':    return row.reference || '—';
        case 'category':     return row.ledger?.name || '—';
        case 'amount':       return this.formatPrice(row.amount);
        case 'currency':     return row.currency ? `${row.currency.name} (${row.currency.symbol})` : '—';
        case 'payment_type': return row.payment_type?.name || '—';
        case 'bank':         return row.bank?.bank_name || '—';
        case 'status':       return this.getStatus(row);
        case 'date':         return this.formatDate(row.transaction_date);
        case 'created_by':   return row.created_user ? `${row.created_user.first_name} ${row.created_user.last_name}`.trim() : '—';
        default:             return '—';
      }
    },

    async doExport() {
      if (!this.selectedExportCols.length) return;
      this.exportLoading   = true;
      this.showExportModal = false;
      try {
        const params = { ...this.tableData, page: 1, length: 10000 };
        const res    = await axios.get('/accounting/expenses', { params });
        const rows   = res.data.data.data;

        const cols    = this.exportColumnDefs.filter(c => this.selectedExportCols.includes(c.key));
        const headers = cols.map(c => c.label);
        const body    = rows.map(row => cols.map(c => this.getExpenseValue(row, c.key)));
        const date    = new Date().toISOString().slice(0, 10);

        if (this.pendingExportFmt === 'csv') {
          const csv  = [headers, ...body]
            .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
            .join('\n');
          const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
          const url  = URL.createObjectURL(blob);
          const a    = document.createElement('a');
          a.href = url; a.download = `expenses-${date}.csv`; a.click();
          URL.revokeObjectURL(url);
        } else {
          const doc       = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
          const usable    = 761;
          const totalW    = cols.reduce((s, c) => s + c.pdfWidth, 0);
          const scale     = usable / totalW;
          const colStyles = {};
          cols.forEach((c, i) => { colStyles[i] = { cellWidth: c.pdfWidth * scale }; });

          doc.setFontSize(18); doc.setFont('helvetica', 'bold'); doc.setTextColor(26, 43, 69);
          doc.text('Expense History', 40, 46);
          doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(122, 144, 176);
          doc.text(`Exported ${new Date().toLocaleDateString('en-GB')}   ·   ${rows.length} record(s)`, 40, 62);

          doc.autoTable({
            startY: 78, head: [headers], body,
            styles: { fontSize: 7.5, cellPadding: 3.5, textColor: [26, 43, 69], overflow: 'ellipsize' },
            headStyles: { fillColor: [0, 74, 173], textColor: 255, fontStyle: 'bold', fontSize: 7.5 },
            alternateRowStyles: { fillColor: [248, 250, 252] },
            columnStyles: colStyles, tableWidth: usable, margin: { left: 40, right: 40 },
            didDrawPage(data) {
              doc.setFontSize(8); doc.setTextColor(192, 202, 214);
              doc.text(`Page ${data.pageNumber}`, doc.internal.pageSize.width / 2, doc.internal.pageSize.height - 16, { align: 'center' });
            },
          });
          doc.save(`expenses-${date}.pdf`);
        }
      } catch { this.toast.error('Export failed. Please try again.'); }
      finally  { this.exportLoading = false; }
    },

    getStatus(data) {
      return data.status == 0 ? 'Pending' : data.status == 1 ? 'Approved' : 'Rejected';
    },

    dataToPrint(details) {
      this.print_content = details.map((expense, idx) => ({
        sn:               idx + 1,
        reference:        expense.reference,
        ledger:           expense?.ledger?.name,
        currency:         `${expense?.currency?.name} (${expense?.currency?.code})`,
        amount:           this.formatPrice(expense.amount),
        bank:             expense?.bank?.bank_name ?? '—',
        payment_type:     expense?.payment_type?.name ?? '—',
        transaction_date: this.formatDate(expense.transaction_date),
        status:           this.getStatus(expense),
      }));
    },

    async fetchData(url = '/accounting/expenses') {
      this.loading = true;
      this.tableData.draw++;
      axios.get(url, { params: this.tableData })
        .then(response => {
          this.expenses = response.data.data.data;
          this.dataToPrint(this.expenses);
          this.configPagination(response.data.data);
          this.loading = false; this.crudLoading = false;
        })
        .catch(() => { this.loading = false; this.crudLoading = false; });
    },

    openExpenseModal(index, type) {
      this.currentData = this.expenses[index];
      this.expense_id  = this.currentData.id;
      this.status_id   = type === 'approve' ? 1 : 2;
      window.$('#approve_modal').modal('show');
    },

    closeApproveModal() { window.$('#approve_modal').modal('hide'); },

    async approveExpense() {
      this.crudLoading = true;
      axios.put('/accounting/expense/update-status', { id: this.expense_id, status: this.status_id })
        .then(response => {
          if (response.data.status == 200) {
            this.crudLoading = false;
            this.fetchData();
            this.closeApproveModal();
            this.toast.success(response.data.message);
          }
        })
        .catch(() => { this.crudLoading = false; });
    },

    getOneModal(data)     { this.expense_id = data.id; },

    async deleteExpense() {
      axios.delete(`/accounting/expense/${this.expense_id}`)
        .then(response => {
          this.fetchData();
          response.data.status == 200
            ? this.toast.success(response.data.message)
            : this.toast.error(response.data.message);
        })
        .catch(() => {});
    },

    viewDetailsModal(index) {
      this.currentData = this.expenses[index];
      window.$('#view_detail').modal('show');
    },

    selectAll() { this.selectedModel = this.allSelected ? this.expenses.map(u => u.id) : []; },
    getUpdateType(type) { this.type = type; },

    bulkUpdate() {
      if (!this.selectedModel.length) { this.toast.error('Nothing selected'); return; }
      axios.post('/accounting/expense/bulk-update', { ids: this.selectedModel, name: this.type })
        .then(response => { this.fetchData(); this.toast.success(response.data.message); this.selectedModel = []; })
        .catch(error => { this.toast.error(error.response?.data?.message ?? 'Something went wrong'); });
    },

    configPagination(data) {
      this.pagination.lastPage    = data.last_page;
      this.pagination.currentPage = data.current_page;
      this.pagination.total       = data.total;
      this.pagination.lastPageUrl = data.last_page_url;
      this.pagination.nextPageUrl = data.next_page_url;
      this.pagination.prevPageUrl = data.prev_page_url;
      this.pagination.from        = data.from;
      this.pagination.to          = data.to;
    },

    formatDate(date) { return moment(date).format('DD/MM/YYYY'); },

    formatPrice(value) {
      if (!value && value !== 0) return '0.00';
      return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
  },
};
</script>

<style scoped>
.page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; margin-bottom:20px; }
.page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub     { font-size:13px; color:#7a90b0; margin:0; }
.page-actions { display:flex; align-items:center; flex-wrap:wrap; gap:8px; }

.search-wrap  { position:relative; display:flex; align-items:center; }
.search-icon  { position:absolute; left:11px; color:#7a90b0; font-size:13px; pointer-events:none; }
.search-input { padding:8px 12px 8px 32px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; width:200px; outline:none; transition:border-color .15s; }
.search-input:focus { border-color:#004AAD; }
.search-input::placeholder { color:#aab8cc; }
.filter-select { padding:8px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; cursor:pointer; }

.btn-primary-action { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:8px 16px; border-radius:8px; border:none; text-decoration:none; cursor:pointer; transition:background .15s; white-space:nowrap; }
.btn-primary-action:hover { background:#003b8e; color:#fff; text-decoration:none; }
.btn-primary-action.disabled { opacity:.5; pointer-events:none; }
.btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:8px 14px; border-radius:8px; cursor:pointer; transition:background .15s; white-space:nowrap; }
.btn-outline:hover { background:#f4f7fb; }
.btn-outline::after { display:none !important; }
.me-1 { margin-right:4px; } .me-2 { margin-right:8px; } .mt-2 { margin-top:8px; } .ms-auto { margin-left:auto; }

.selection-badge { background:#004AAD; color:#fff; border-radius:20px; padding:1px 7px; font-size:11px; font-weight:700; margin-left:4px; }

.data-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }

.state-block { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
.state-icon  { width:60px; height:60px; border-radius:16px; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
.state-icon--orange { background:rgba(224,140,0,.1); color:#e08c00; }
.state-title { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
.state-sub   { font-size:13px; color:#7a90b0; margin:0; }
.mt-3        { margin-top:12px; }

.table-toolbar { display:flex; align-items:center; justify-content:space-between; padding:14px 20px; border-bottom:1px solid #e4eaf2; flex-wrap:wrap; gap:10px; }
.table-count   { font-size:13px; color:#7a90b0; }
.toolbar-right { display:flex; align-items:center; gap:8px; }
.rows-select   { border:1px solid #dde5f0; border-radius:7px; padding:5px 10px; font-size:13px; color:#1a2b45; background:#f8fafc; outline:none; cursor:pointer; }

.table-wrap  { overflow-x:auto; }
.data-table  { width:100%; border-collapse:collapse; }
.data-table thead tr { background:#f8fafc; }
.data-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:12px 14px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
.data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .12s; }
.data-table tbody tr:last-child { border-bottom:none; }
.data-table tbody tr:hover { background:#f8fafc; }
.data-table tbody td { padding:12px 14px; font-size:13.5px; vertical-align:middle; }
.col-actions { width:160px; }
.text-right      { text-align:right; }
.text-muted-cell { color:#4a5e78; }
.fw-bold         { font-weight:700; }
.amount-value    { color:#004AAD; }

.batch-code { font-family:monospace; font-size:12.5px; font-weight:600; color:#1a2b45; background:#f4f7fb; border:1px solid #e4eaf2; border-radius:6px; padding:2px 8px; }

.status-badge     { display:inline-block; border-radius:6px; padding:3px 10px; font-size:12px; font-weight:600; border:1px solid transparent; text-transform:capitalize; }
.status--pending  { background:#fff7ed; color:#e08c00; border-color:#fed7aa; }
.status--approved { background:#f0fdf4; color:#16a34a; border-color:#bbf7d0; }
.status--rejected { background:#fff0f0; color:#e03535; border-color:#fac5c5; }

/* ── Row Action Dropdown ── */
.action-dropdown-btn { width:30px; height:30px; border-radius:8px; border:1px solid #dde5f0; background:#fff; display:flex; align-items:center; justify-content:center; font-size:13px; color:#7a90b0; cursor:pointer; transition:background .15s; }
.action-dropdown-btn:hover { background:#f4f7fb; color:#1a2b45; }
.action-menu { min-width:180px; padding:6px; border:1px solid #e4eaf2; border-radius:10px; box-shadow:0 8px 24px rgba(0,0,0,.1); }
.action-menu-item { display:flex; align-items:center; gap:10px; width:100%; padding:8px 12px; font-size:13px; color:#1a2b45; background:transparent; border:none; border-radius:7px; cursor:pointer; text-decoration:none; transition:background .12s; }
.action-menu-item:hover:not(:disabled):not(.action-menu-item--disabled) { background:#f4f7fb; text-decoration:none; color:#1a2b45; }
.action-menu-item:disabled,.action-menu-item--disabled { opacity:.4; cursor:not-allowed; pointer-events:none; }
.action-menu-item--danger { color:#e03535; } .action-menu-item--danger:hover { background:#fff0f0 !important; }
.action-menu-icon         { font-size:12px; width:16px; flex-shrink:0; }
.action-menu-icon--view   { color:#0d9488; }
.action-menu-icon--edit   { color:#004AAD; }
.action-menu-icon--approve { color:#16a34a; }
.action-menu-icon--reject  { color:#e08c00; }

.table-footer { padding:14px 20px; border-top:1px solid #e4eaf2; display:flex; justify-content:flex-end; }

.dropdown-menu    { border:1px solid #e4eaf2; border-radius:9px; box-shadow:0 8px 24px rgba(0,0,0,.08); padding:6px; min-width:170px; }
.dropdown-item    { border-radius:6px; font-size:13px; padding:8px 12px; color:#1a2b45; background:transparent; border:none; width:100%; text-align:left; cursor:pointer; display:flex; align-items:center; }
.dropdown-item:hover { background:#f4f7fb; }
.dropdown-item:disabled { opacity:.4; cursor:not-allowed; }
.dropdown-divider { border-color:#f0f4f8; margin:4px 0; }

/* ── Approve/Reject Modal ── */
.modal-clean { border:none; border-radius:16px; box-shadow:0 20px 60px rgba(0,0,0,.14); overflow:hidden; }
.modal-icon-wrap { display:flex; justify-content:center; padding:28px 24px 0; }
.modal-icon { width:60px; height:60px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:26px; }
.modal-icon--success { background:rgba(22,163,74,.1);  color:#16a34a; }
.modal-icon--warning { background:rgba(224,140,0,.1);  color:#e08c00; }
.modal-body-clean { padding:16px 28px 8px; text-align:center; }
.modal-heading    { font-size:17px; font-weight:700; color:#1a2b45; margin:0 0 8px; }
.modal-subtext    { font-size:13.5px; color:#7a90b0; margin:0; line-height:1.6; }
.modal-actions    { display:flex; gap:10px; padding:18px 24px 24px; justify-content:center; }
.modal-btn        { display:inline-flex; align-items:center; justify-content:center; font-size:13px; font-weight:600; padding:9px 22px; border-radius:8px; border:none; cursor:pointer; flex:1; }
.modal-btn--cancel  { background:#f4f7fb; color:#1a2b45; border:1px solid #dde5f0; }
.modal-btn--confirm { background:#004AAD; color:#fff; }
.modal-btn--success { background:#16a34a !important; color:#fff; }
.modal-btn--warning { background:#e08c00 !important; color:#fff; }
.modal-btn:disabled { opacity:.6; cursor:not-allowed; }

/* ── View Detail Modal ── */
.modal-header-clean { display:flex; align-items:flex-start; gap:14px; padding:20px 24px 16px; border-bottom:1px solid #f0f4f8; }
.modal-title-icon { width:40px; height:40px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:16px; flex-shrink:0; }
.modal-icon--success-bg { background:rgba(22,163,74,.1); color:#16a34a; }
.modal-icon--warning-bg { background:rgba(224,140,0,.1); color:#e08c00; }
.modal-icon--danger-bg  { background:rgba(224,53,53,.1); color:#e03535; }
.modal-clean-title { font-size:14px; font-weight:700; color:#1a2b45; margin:0 0 2px; }
.modal-clean-sub   { font-size:12px; color:#7a90b0; margin:0; }

.detail-grid    { display:grid; grid-template-columns:1fr 1fr; gap:16px; padding:4px 0; }
.detail-item    { display:flex; flex-direction:column; gap:5px; }
.detail-item--full { grid-column:1/-1; }
.detail-label   { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; }
.detail-value   { font-size:13.5px; font-weight:600; color:#1a2b45; }
.detail-value--muted { color:#4a5e78; font-weight:400; line-height:1.6; }
.detail-divider { grid-column:1/-1; height:1px; background:#f0f4f8; margin:4px 0; }

@media(max-width:991px) { .page-header,.page-actions { flex-direction:column; align-items:stretch; } .search-input { width:100%; } .detail-grid { grid-template-columns:1fr; } }

/* -- Column picker modal ------------------------------------------------- */
.col-picker-backdrop { position:fixed; inset:0; background:rgba(15,25,40,.45); z-index:1050; display:flex; align-items:center; justify-content:center; padding:20px; }
.col-picker-modal    { background:#fff; border-radius:14px; width:100%; max-width:480px; box-shadow:0 24px 60px rgba(0,0,0,.18); overflow:hidden; }
.col-picker-header   { display:flex; align-items:flex-start; justify-content:space-between; padding:20px 22px 14px; border-bottom:1px solid #f0f4f9; }
.col-picker-title    { font-size:15px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.col-picker-sub      { font-size:12px; color:#7a90b0; margin:0; }
.col-picker-close    { background:none; border:none; color:#7a90b0; font-size:15px; cursor:pointer; padding:4px; line-height:1; }
.col-picker-close:hover { color:#1a2b45; }
.col-picker-actions  { display:flex; align-items:center; gap:8px; padding:10px 22px; background:#f8fafc; font-size:12px; border-bottom:1px solid #f0f4f9; }
.col-picker-link     { background:none; border:none; color:#004AAD; font-size:12px; cursor:pointer; padding:0; font-weight:600; font-family:inherit; }
.col-picker-link:hover { text-decoration:underline; }
.col-picker-sep      { color:#c5d3e0; }
.col-picker-count    { margin-left:auto; color:#7a90b0; font-size:11.5px; }
.col-picker-grid     { display:grid; grid-template-columns:1fr 1fr; gap:2px; padding:14px 16px; max-height:280px; overflow-y:auto; }
.col-picker-item     { display:flex; align-items:center; gap:9px; padding:9px 10px; border-radius:8px; cursor:pointer; transition:background .1s; }
.col-picker-item:hover { background:#f4f7fb; }
.col-picker-check    { width:15px; height:15px; accent-color:#004AAD; cursor:pointer; flex-shrink:0; }
.col-picker-label    { font-size:13px; color:#1a2b45; user-select:none; }
.col-picker-footer   { display:flex; justify-content:flex-end; gap:8px; padding:14px 20px; border-top:1px solid #f0f4f9; }
.fade-enter-active, .fade-leave-active { transition:opacity .15s ease; }
.fade-enter, .fade-leave-to { opacity:0; }
</style>